import Unicode from 'common/utils/unicode';
import I18n from 'i18n-js';
import {
  maxDecimalPointsValidator,
  mustBeGreaterThanValidator,
} from '../../../../common/form-validators/form-validators';

const stepDetailFieldPrefix = 'step';

export const maxLengthRule = (maxLength = 120) => ({
  max: maxLength,
  message: I18n.t(
    'phrase.geWebBulkUpload.express_web_bulk_upload_line_error_character_limit_error',
    {
      maxLength,
    },
  ),
});

export const phoneRule = (minLength = 5, maxLength = 15) => ({
  pattern: new RegExp(`^[0-9]{${minLength},${maxLength}}$`),
  message: I18n.t(
    'phrase.geWebBulkUpload.express_web_bulk_upload_line_error_valid_phone_number_error',
  ),
});

export const deliveryFormFields = {
  // service config
  vehicleType: {
    fieldName: 'vehicleType',
  },
  // delivery step fields
  locationNote: {
    fieldName: 'locationNote',
    maxLength: 120,
    makeRules: ({ maxLength = 120 }) => [maxLengthRule(maxLength)],
  },
  name: {
    fieldName: 'name',
    makeRules: ({ maxLength = 50 }) => [
      {
        // pattern include: letters of all languages,
        // space, single quote, double quote
        pattern: new RegExp(`^[${Unicode.w} '"]+$`),
        message: I18n.t(
          'phrase.geWebBulkUpload.express_web_bulk_upload_line_error_letters_numbers_error',
        ),
      },
      {
        required: true,
        message: I18n.t(
          'phrase.geWebBulkUpload.express_web_bulk_upload_line_error_field_required_error',
        ),
      },
      {
        max: maxLength,
        message: I18n.t(
          'phrase.geWebBulkUpload.express_web_bulk_upload_line_error_character_limit_error',
          {
            maxLength,
          },
        ),
      },
    ],
  },
  phone: {
    fieldName: 'phone',
    makeRules: ({ minLength = 5, maxLength = 15 }) => [
      {
        required: true,
        message: I18n.t(
          'phrase.geWebBulkUpload.express_web_bulk_upload_line_error_field_required_error',
        ),
      },
      phoneRule(minLength, maxLength),
    ],
  },
  cod: {
    fieldName: 'cod',
    makeRules: ({ lowerLimit, upperLimit, compulsoryCOD, disabled }) => [
      {
        required: !disabled && compulsoryCOD,
        message: I18n.t(
          'phrase.geWebBulkUpload.express_web_bulk_upload_line_error_field_required_error',
        ),
      },
      (rule, value, callback) => {
        if (!value || value === 0 || value === '0') {
          callback();
          return;
        }
        if (lowerLimit !== 0 && value < lowerLimit) {
          callback(
            I18n.t(
              'phrase.geWebBulkUpload.express_web_bulk_upload_line_error_valid_amount_error',
            ),
          );
          return;
        }
        if (upperLimit !== 0 && value > upperLimit) {
          callback(
            I18n.t(
              'phrase.geWebBulkUpload.express_web_bulk_upload_line_error_amount_limit_error',
              { maxAmount: upperLimit },
            ),
          );
          return;
        }
        callback();
      },
    ],
  },
  // delivery detail
  itemWeight: {
    fieldName: 'itemWeight',
    makeRules: ({ maxWeight }) => {
      const validators = [
        (rule, value, callback) => {
          if (!value || isNaN(value)) {
            callback(
              I18n.t(
                'phrase.geWebBulkUpload.express_web_bulk_upload_line_error_select_valid_weight',
              ),
            );
            return;
          }
          if (Number(value) > maxWeight) {
            callback(
              I18n.t(
                'phrase.geWebBulkUpload.express_web_bulk_upload_line_error_amount_limit_error',
                { maxAmount: maxWeight },
              ),
            );
            return;
          }
          callback();
        },
      ];

      // TODO: update phrase localization
      validators.push(
        mustBeGreaterThanValidator(0, 'Enter a valid weight'),
        maxDecimalPointsValidator(0, 'Decimal points not allowed'),
      );

      return validators;
    },
  },
  itemType: {
    fieldName: 'itemType',
  },
  itemTypeFreeText: {
    fieldName: 'itemTypeFreeText',
  },
  deliveryInstruction: {
    fieldName: 'deliveryInstruction',
    makeRules: () => [maxLengthRule(120)],
  },
  paymentTypeID: {
    fieldName: 'paymentTypeID',
    makeRules: () => [
      {
        required: true,
        message: I18n.t(
          'phrase.geWebBulkUpload.express_web_bulk_upload_line_error_field_required_error',
        ),
      },
    ],
  },
  referenceCode: {
    fieldName: 'referenceCode',
    makeRules: () => [maxLengthRule(120)],
  },
  promoCode: {
    fieldName: 'promoCode',
    makeRules: () => [maxLengthRule(20)],
  },
  tripCode: {
    fieldName: 'tripCode',
    makeRules: () => [maxLengthRule(120)],
  },
  tripDescription: {
    fieldName: 'tripDescription',
    makeRules: () => [maxLengthRule(120)],
  },
  parcelTotalSize: {
    fieldName: 'parcelTotalSize',
    makeRules: ({ parcelDisplaySizes }) => [
      {
        required: true,
      },
      (rule, value, callback) => {
        if (!value) {
          callback(
            I18n.t(
              'phrase.geWebBulkUpload.express_web_bulk_upload_line_error_valid_amount_error',
            ),
          );
          return;
        }
        if (parcelDisplaySizes.indexOf(value) < 0) {
          callback(
            I18n.t(
              'phrase.geWebBulkUpload.express_web_bulk_upload_line_error_valid_amount_error',
            ),
          );
          return;
        }
        callback();
      },
    ],
  },
};

export const makeStepDetailFieldName = (name, isPickUp, index = 0) =>
  `${stepDetailFieldPrefix}-${
    isPickUp ? 'pickUp' : 'dropOff'
  }-${index}-${name}`;

const otherDeliveryDetailFieldNames = [
  deliveryFormFields.itemWeight.fieldName,
  deliveryFormFields.itemType.fieldName,
  deliveryFormFields.itemTypeFreeText.fieldName,
  deliveryFormFields.parcelTotalSize.fieldName,
  deliveryFormFields.deliveryInstruction.fieldName,
  deliveryFormFields.paymentTypeID.fieldName,
  deliveryFormFields.referenceCode.fieldName,
  deliveryFormFields.promoCode.fieldName,
  deliveryFormFields.tripCode.fieldName,
  deliveryFormFields.tripDescription.fieldName,
  deliveryFormFields.tripDescription.parcelTotalSize,
];
export const isOtherDeliveryDetailField = fieldName =>
  otherDeliveryDetailFieldNames.indexOf(fieldName) > -1;

export const isStepDetailField = fieldName =>
  fieldName && fieldName.startsWith(stepDetailFieldPrefix);

export const parseStepDetailField = oFieldName => {
  // eslint-disable-next-line no-unused-vars
  const [_, type, index, fieldName] = oFieldName.split('-');
  return {
    isPickUp: type === 'pickUp',
    index: +index,
    fieldName,
  };
};

export default {};
